// Home page sections (plan H, docs/oct27-work/plans/H.md §2.1, §4.3): the pure
// part of the admin screen. The API is the real boundary (BE home-sections
// module); everything here only decides what the screen shows and how a
// toggle, its rollback and the server's answer change the rows.
import { format } from 'date-fns';
import { apiErrorText } from 'src/utils/apiErrorText';

// The same literal lives in the API (home-sections.registry.ts) and the site
// (src/utils/homeSections.ts); each repo's unit test pins it, so a drift fails
// in all three. Page order. `hero` is deliberately absent: it always shows.
export const HOME_SECTION_KEYS = [
  'events',
  'partners',
  'diplomaFeedback',
  'successStories',
  'kayfaFamily',
  'stats',
  'certifiedGraduates',
  'learningPaths',
  'instructors',
  'liveSessions',
  'pricing',
  'contact',
] as const;

export type HomeSectionKey = typeof HOME_SECTION_KEYS[number];

// Mirrors BE HOME_SECTION_ROLES. super-admin passes every guard on its own; it
// is listed so the nav item, the route guard and this check read the same list.
// No country scoping: the home page is one page for both markets (HS-11b).
export const HOME_SECTION_ROLES = ['admin', 'super-admin', 'marketing'];

export function canManageHomeSections(roles: string[] | null | undefined): boolean {
  return Array.isArray(roles) && roles.some((r) => HOME_SECTION_ROLES.indexOf(r) >= 0);
}

export const LABELS: Record<HomeSectionKey, { label: string; labelAr: string }> = {
  events: { label: 'Program banner', labelAr: 'بانر البرنامج' },
  partners: { label: 'Partners', labelAr: 'الشركاء' },
  diplomaFeedback: { label: 'Diploma student feedback', labelAr: 'ماذا يقول طلاب الدبلومات' },
  successStories: { label: 'Success stories', labelAr: 'قصص نجاح' },
  kayfaFamily: { label: 'The Kayfa family (NGen + Community)', labelAr: 'عائلة كيف' },
  stats: { label: 'Impact in numbers', labelAr: 'الأرقام والإحصاءات' },
  certifiedGraduates: { label: 'Certified graduates', labelAr: 'خريجون معتمدون' },
  learningPaths: { label: 'Learning paths', labelAr: 'المسارات التعليمية' },
  instructors: { label: 'Instructors', labelAr: 'تعلّم من خبراء المجال' },
  liveSessions: { label: 'Live sessions', labelAr: 'الجلسات المباشرة' },
  pricing: { label: 'Pricing plans', labelAr: 'باقات الأسعار' },
  contact: { label: 'Contact us', labelAr: 'اتصل بنا' },
};

export const NOTES: Partial<Record<HomeSectionKey, string>> = {
  events: 'Only signed-in students assigned to a program ever see it.',
  diplomaFeedback:
    'Alumni stories with a quote from published diploma pages (Diplomas › landing page › Alumni stories). Shows only when there is at least one.',
  successStories: 'Shows only when at least one active story or activity exists.',
  certifiedGraduates:
    'Stays hidden on the site until a real graduate is listed, whatever this switch says.',
  liveSessions: 'Hidden by default.',
  pricing: 'Upgrade buttons on courses without their own package open this section (/#pricing).',
  contact:
    'The Contact us page stays available. Links to kayfa.io/#contact land at the top of the home page while it is hidden.',
};

export const UNKNOWN_NOTE = 'New section: update the admin labels';

const isKnownKey = (key: string): key is HomeSectionKey =>
  (HOME_SECTION_KEYS as readonly string[]).indexOf(key) >= 0;

/** The row's labels and note. A key the admin does not know yet shows as-is. */
export function sectionInfo(key: string): {
  label: string;
  labelAr: string;
  note: string;
  known: boolean;
} {
  if (!isKnownKey(key)) return { label: key, labelAr: '', note: UNKNOWN_NOTE, known: false };
  return { ...LABELS[key], note: NOTES[key] || '', known: true };
}

// HS-13: hiding Pricing breaks nothing, but it changes where upgrade buttons land.
export const PRICING_CONFIRM_TITLE = 'Hide Pricing plans?';
export const PRICING_CONFIRM_TEXT =
  'Upgrade buttons on courses without their own package open this section (/#pricing). ' +
  'While it is hidden they land at the top of the home page.';

/** True when the change must be confirmed first (switching Pricing off). */
export function needsConfirm(key: string, nextVisible: boolean): boolean {
  return key === 'pricing' && nextVisible === false;
}

// ---- Rows ---------------------------------------------------------------------

export type HomeSectionRow = {
  key: string;
  visible: boolean;
  isDefault: boolean;
  defaultVisible: boolean;
  changedAt: string | null;
  changedByEmail: string | null;
  // A PATCH for this row is in flight: its switch is disabled.
  pending?: boolean;
};

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

function rowFrom(raw: unknown): HomeSectionRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.key !== 'string' || !r.key || typeof r.visible !== 'boolean') return null;
  return {
    key: r.key,
    visible: r.visible,
    isDefault: r.isDefault === true,
    defaultVisible: typeof r.defaultVisible === 'boolean' ? r.defaultVisible : r.visible,
    changedAt: str(r.changedAt),
    changedByEmail: str(r.changedByEmail),
    pending: false,
  };
}

/**
 * The rows of a GET admin (or PATCH) answer, `{ sections: [...] }`, in the
 * server's order. null when the answer is not usable: the screen then shows an
 * error, never the defaults as if they were the saved state (HS-10).
 */
export function rowsFrom(data: unknown): HomeSectionRow[] | null {
  if (!data || typeof data !== 'object') return null;
  const { sections } = data as { sections?: unknown };
  if (!Array.isArray(sections)) return null;
  const rows = sections.map(rowFrom).filter((r): r is HomeSectionRow => r !== null);
  return rows.length ? rows : null;
}

/** The switch flips at once and stays disabled until its request settles. */
export function toggleState(
  rows: HomeSectionRow[],
  key: string,
  nextVisible?: boolean
): HomeSectionRow[] {
  return rows.map((r) =>
    r.key === key
      ? {
          ...r,
          visible: typeof nextVisible === 'boolean' ? nextVisible : !r.visible,
          pending: true,
        }
      : r
  );
}

/** The request failed: the row goes back to its previous value. */
export function rollbackState(
  rows: HomeSectionRow[],
  key: string,
  previousVisible: boolean
): HomeSectionRow[] {
  return rows.map((r) => (r.key === key ? { ...r, visible: previousVisible, pending: false } : r));
}

/**
 * A PATCH succeeded. Its answer lists every section as the server saw it at
 * that write, but answers of quick toggles can arrive in any order, so ONLY
 * the written key is taken from it (HS-9b). Other rows stay as they are on
 * screen; the GET after the last pending write reconciles them.
 */
export function applyResult(
  rows: HomeSectionRow[],
  key: string,
  response: unknown
): HomeSectionRow[] {
  const saved = (rowsFrom(response) || []).find((r) => r.key === key);
  return rows.map((r) => {
    if (r.key !== key) return r;
    return saved ? { ...saved, pending: false } : { ...r, pending: false };
  });
}

/**
 * A full list from a GET admin may replace the screen only when it is the
 * newest GET issued and no PATCH is in flight (HS-9b): an older or racing
 * answer would revert a switch that was just changed.
 */
export function loadIsCurrent(seq: number, latestSeq: number, pendingWrites: number): boolean {
  return seq === latestSeq && pendingWrites === 0;
}

// ---- Errors -------------------------------------------------------------------
// The axios instance rejects with the response BODY (utils/axios.ts), never the
// HTTP status, and with the string 'Something went wrong' when no response came
// back at all (network drop, timeout) or the body was empty. The API's error
// bodies are JSON objects: `{ error, message | messages, code? }`.

const statusOf = (e: any): number | null => {
  const s = e?.response?.status ?? e?.statusCode ?? e?.status;
  return typeof s === 'number' ? s : null;
};

// A gateway answer (bad gateway, unavailable, timeout) can come after the API wrote.
const GATEWAY_STATUSES = [502, 503, 504];

/**
 * True when the failure says nothing about whether the write happened: no
 * HTTP answer (network, timeout), a non-JSON body (a proxy page) or a gateway
 * status. The screen then reloads the saved state instead of trusting the
 * rollback (HS-9c). A JSON 4xx/5xx from the API means nothing was written.
 */
export function isUnconfirmedFailure(e: unknown): boolean {
  if (!e || typeof e !== 'object') return true;
  const status = statusOf(e);
  if (status !== null) return status === 0 || GATEWAY_STATUSES.indexOf(status) >= 0;
  const o = e as any;
  if (e instanceof Error || o.isAxiosError || o.request) return true;
  return !('error' in o || 'message' in o || 'messages' in o || 'code' in o);
}

const CODE_TEXT: Record<string, string> = {
  HomeSectionUnknown: 'The server does not know this section. Reload the page and try again.',
  HomeSectionLocked: 'The top banner (hero) always shows and cannot be hidden.',
  HomeSectionInvalid: 'The change was not valid. Reload the page and try again.',
};

const isForbidden = (e: any): boolean =>
  statusOf(e) === 403 || e?.error === 'ForbiddenException' || e?.error === 'Forbidden';

const isUnauthorized = (e: any): boolean =>
  statusOf(e) === 401 || e?.error === 'UnauthorizedException' || e?.error === 'Unauthorized';

/** One readable line for the Alert above the table: the server's message, plus its code. */
export function errorText(e: unknown): string {
  if (isUnconfirmedFailure(e)) {
    return 'The server did not answer (network error or timeout).';
  }
  const o = e as any;
  if (isForbidden(o)) return 'You do not have access to change home page sections.';
  if (isUnauthorized(o)) return 'Your session has ended. Sign in again.';
  const code = typeof o.code === 'string' ? o.code : '';
  const text = (code && CODE_TEXT[code]) || apiErrorText(o, 'Something went wrong');
  return code ? `${text} (${code})` : text;
}

export const UNCONFIRMED_TEXT = 'Could not confirm the change; the list shows the saved state.';

/** The snackbar after a saved change: on-demand revalidation worked, or the 60 s fallback. */
export function saveMessage(revalidated: boolean): string {
  return revalidated
    ? 'Saved. Live on the site now.'
    : 'Saved. The site shows it within about a minute.';
}

/** "Hidden by <email> · 27 Oct 2026 14:02", or "Default" while the code default applies. */
export function lastChangeText(row: HomeSectionRow): string {
  if (row.isDefault) return 'Default';
  const state = row.visible ? 'Shown' : 'Hidden';
  const by = row.changedByEmail ? ` by ${row.changedByEmail}` : '';
  const at = row.changedAt ? new Date(row.changedAt) : null;
  const when = at && !Number.isNaN(at.getTime()) ? ` · ${format(at, 'd MMM yyyy HH:mm')}` : '';
  return `${state}${by}${when}`;
}
